import { Form, Layout } from '@shopify/polaris'
import { Formik, useFormikContext } from 'formik'
import { useCallback, useEffect, useRef, useState } from 'react'

import { Result } from '../common/api'
import { gidToId, ResultToastOrBanner } from '../common/helpers'
import { useSaveBarContext } from '../common/SaveBar'
import * as urls from '../common/urls'
import { FormikSaveBar } from '../main/SaveBarProvider'
import { createSellingPlanGroup, updateSellingPlanGroup, useSellingPlanWorkflowsSWR } from './api'
import {
  parseSellingPlanGroupResponse,
  SellingPlanGroupFormData,
  sellingPlanGroupFormDataWithDefaultValues,
  validateSellingPlanGroupFormData,
} from './models-form'
import { sellingPlanGroupFormDataToInput, sellingPlanGroupFormDataToPWData } from './models-input'
import { OverviewCard } from './OverviewCard'
import { VariantCard } from './VariantCard'

const ErrorBanner = ({ setSaveResult }: { setSaveResult: (res: Result<any> | null) => void }) => {
  const { submitCount, validateForm } = useFormikContext<SellingPlanGroupFormData>()
  const prevSubmitCount = useRef(submitCount)

  useEffect(() => {
    if (submitCount === prevSubmitCount.current) return

    prevSubmitCount.current = submitCount

    validateForm().then((errors) => {
      if (Object.keys(errors).length > 0) {
        setSaveResult({
          status: 'error',
          message: 'There are errors in the form. Please review and fix them before saving.',
          temporary: false,
        })
        window.scrollTo(0, 0)
      }
    })
  }, [submitCount, validateForm, setSaveResult])

  return null
}

export const SellingPlanGroupForm = ({
  initialValues,
  afterSave,
}: {
  initialValues: SellingPlanGroupFormData
  afterSave: (groupId: string) => any
}) => {
  const sellingPlanWorkflowsSWR = useSellingPlanWorkflowsSWR(gidToId(initialValues.id ?? ''))
  const { hideAndRedirect } = useSaveBarContext()
  const [saveResult, setSaveResult] = useState<Result<any> | null>(null)

  const handleSubmit = useCallback(
    async (values: SellingPlanGroupFormData, actions: any) => {
      const id = values.id
      const fixedValues = sellingPlanGroupFormDataWithDefaultValues(values)

      const validationError = validateInput(fixedValues)

      if (validationError) {
        setSaveResult({
          status: 'error',
          message: validationError,
          temporary: false,
        })
        window.scrollTo(0, 0)
        return
      }

      const input = sellingPlanGroupFormDataToInput(fixedValues)

      const pwData = sellingPlanGroupFormDataToPWData(fixedValues)
      const res = id
        ? await updateSellingPlanGroup(id, input, pwData)
        : await createSellingPlanGroup(input, pwData)

      actions.setSubmitting(false)

      setSaveResult(res)

      if (res.status === 'success' && res.data) {
        afterSave(res.data.gid)
        actions.resetForm({ values: parseSellingPlanGroupResponse(res.data) })
      } else {
        window.scrollTo(0, 0)
      }
    },
    [afterSave]
  )

  const validateInput = (formData: SellingPlanGroupFormData) => {
    const invalidPlan = formData.sellingPlans.find(
      (plan) => plan.type === 'specific-dates' && plan.deliveryPolicy.recurring.anchors.length === 0
    )

    if (invalidPlan) {
      return `${invalidPlan.name} plan must have at least one anchor date`
    }

    return null
  }

  const handleDiscard = useCallback(() => {
    if (!initialValues.id) {
      hideAndRedirect(urls.plansUrl, 'replace')
    }
  }, [initialValues.id, hideAndRedirect])

  return (
    <div style={{ flexGrow: 1 }}>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize
        validate={validateSellingPlanGroupFormData}
        validateOnChange={false}
        validateOnBlur={true}
      >
        {(formik) => (
          <>
            <ErrorBanner setSaveResult={setSaveResult} />
            <Form onSubmit={formik.handleSubmit}>
              <Layout.Section>
                <ResultToastOrBanner result={saveResult} setResult={setSaveResult} />

                <OverviewCard />

                {formik.values.sellingPlans.map((_plan: any, index: number) => (
                  <VariantCard
                    key={index}
                    index={index}
                    sellingPlanWorkflowsSWR={sellingPlanWorkflowsSWR}
                  />
                ))}
              </Layout.Section>
            </Form>
            <FormikSaveBar isNew={!initialValues.id} onDiscard={handleDiscard} />
          </>
        )}
      </Formik>
    </div>
  )
}
